import React, { useState, useMemo, useEffect } from "react";
import api from "../../utils/api";
import Stepper from "../../components/Stepper";
import DevicePreviewFrame from "../../components/DevicePreviewFrame";
import { POPUP_TEMPLATES, POPUP_FIELD_DEFAULTS, applyPopupAdvancedStyles, applyScrollableWidgetBackground, generateDesktopLPPage } from "../../utils/templates";
import { downloadAsZip } from "../../utils/zipHelper";

// No "generate" step — the README is explicit that the popup module skips
// AI generation entirely: Domain -> Choose a design -> Live editor -> Preview & Save.
const STEPS = ["Domain", "Choose a Design", "Live Editor", "Preview & Save"];

const POPUP_SCROLL_BG_DEFAULTS = {
  scrollBgEnabled: false,
  scrollBgType: "color",
  scrollBgColor: "#f8fafc",
  scrollBgDesktopImages: ["", "", ""],
  scrollBgPhoneImages: ["", "", ""],
  scrollBgBlur: 0,
  scrollBgOpacity: 0.25
};

function TypographyRow({ title, form, setForm, sizeKey, weightKey, formatKey }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-600 mb-1.5">{title}</p>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Size (px)</label>
          <input type="number" className="input text-xs" placeholder="auto" value={form[sizeKey]}
            onChange={e => setForm({ ...form, [sizeKey]: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Weight</label>
          <select className="input text-xs" value={form[weightKey]}
            onChange={e => setForm({ ...form, [weightKey]: e.target.value })}>
            <option value="">Default</option>
            <option value="400">Normal</option>
            <option value="500">Medium</option>
            <option value="600">Semibold</option>
            <option value="700">Bold</option>
            <option value="800">Extrabold</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Format</label>
          <select className="input text-xs" value={form[formatKey]}
            onChange={e => setForm({ ...form, [formatKey]: e.target.value })}>
            <option value="normal">Normal</option>
            <option value="uppercase">Uppercase</option>
            <option value="italic">Italic</option>
          </select>
        </div>
      </div>
    </div>
  );
}

export default function PopupModule() {
  const [instance, setInstance] = useState(0);
  return <PopupModuleInner key={instance} onAddNew={() => setInstance(i => i + 1)} />;
}

function PopupModuleInner({ onAddNew }) {
  const [step, setStep]         = useState(0);
  const [domain, setDomain]     = useState("");
  const [templateId, setTemplateId] = useState(null);
  const [form, setForm]         = useState({ ...POPUP_FIELD_DEFAULTS, ...POPUP_SCROLL_BG_DEFAULTS });
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState("");

  // Project-name availability — names already used by this user for this module.
  const [takenNames, setTakenNames] = useState([]);
  useEffect(() => {
    api.get("/api/pages")
      .then(({ data }) => setTakenNames(
        (Array.isArray(data) ? data : [])
          .filter(p => p.type === "popup")
          .map(p => (p.domain || "").trim().toLowerCase())
      ))
      .catch(() => {});
  }, []);
  const nameValue = domain.trim();
  const nameExists = nameValue !== "" && takenNames.includes(nameValue.toLowerCase());
  const nameAvailable = nameValue !== "" && !nameExists;

  // "Create LP for Desktop" feature
  const [desktopLP, setDesktopLP] = useState(false);
  const [blogLP, setBlogLP]       = useState(null);
  const [lpDomain, setLpDomain]   = useState("");
  const [lpIndustry, setLpIndustry] = useState("");
  const [lpLoading, setLpLoading] = useState(false);
  const [lpError, setLpError]     = useState("");
  const [lpImage, setLpImage]     = useState("");
  const [previewMode, setPreviewMode] = useState("desktop");

  const template = POPUP_TEMPLATES.find(t => t.id === templateId);

  const popupHtml = useMemo(() => {
    if (!template) return "";
    const html = template.generate({ ...form, domain });
    const styled = applyPopupAdvancedStyles(html, {
      enabled: form.advancedEnabled,
      headingColor: form.headingColor, subColor: form.subColor,
      fontSize: form.fontSize, fontWeight: form.fontWeight, format: form.format,
      subFontSize: form.subFontSize, subFontWeight: form.subFontWeight, subFormat: form.subFormat,
      btnFontSize: form.btnFontSize, btnFontWeight: form.btnFontWeight, btnFormat: form.btnFormat,
      buttonWidth: form.buttonWidth, buttonPaddingX: form.buttonPaddingX, buttonPaddingY: form.buttonPaddingY,
      btnRadius: form.btnRadius, boxShadow: form.boxShadow,
      contentAlign: form.contentAlign, buttonLayout: form.buttonLayout
    });
    return applyScrollableWidgetBackground(styled, {
      enabled: form.scrollBgEnabled,
      type: form.scrollBgType,
      color: form.scrollBgColor,
      desktopImages: form.scrollBgDesktopImages,
      phoneImages: form.scrollBgPhoneImages,
      blur: form.scrollBgBlur,
      opacity: form.scrollBgOpacity,
      overlaySelector: "#popup-overlay",
      modalSelector: "#popup-modal"
    });
  }, [template, form, domain]);

  const lpActive = desktopLP && !!blogLP;
  const lpBlog = lpImage && blogLP ? { ...blogLP, imageUrl: lpImage } : blogLP;

  const outputHtml = useMemo(() =>
    lpActive ? generateDesktopLPPage({ blog: lpBlog, consentHtml: popupHtml, domain: lpDomain || domain }) : popupHtml,
    [lpActive, lpBlog, popupHtml, lpDomain, domain]);

  const previewHtml = useMemo(() =>
    lpActive ? generateDesktopLPPage({ blog: lpBlog, consentHtml: popupHtml, domain: lpDomain || domain, mode: previewMode }) : popupHtml,
    [lpActive, lpBlog, popupHtml, lpDomain, domain, previewMode]);

  const handleLpImage = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const r = new FileReader();
    r.onload = () => setLpImage(r.result);
    r.readAsDataURL(file);
  };

  const generateLanding = async () => {
    const targetDomain = (lpDomain || domain).trim();
    if (!targetDomain) return;
    setLpError(""); setLpLoading(true);
    try {
      const { data } = await api.post("/api/generate/landing", { domain: targetDomain, industry: lpIndustry.trim() });
      setBlogLP(data);
    } catch (err) {
      setLpError(err.response?.data?.message || "Landing page generation failed");
    } finally { setLpLoading(false); }
  };

  const handleImageUpload = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, imageUrl: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleScrollBgImage = (file, index, target = "phone") => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm(f => {
        const key = target === "desktop" ? "scrollBgDesktopImages" : "scrollBgPhoneImages";
        const images = [...(f[key] || ["", "", ""])];
        images[index] = reader.result;
        return { ...f, [key]: images, scrollBgType: "image" };
      });
    };
    reader.readAsDataURL(file);
  };

  const chooseTemplate = (id) => {
    const t = POPUP_TEMPLATES.find(t => t.id === id);
    setTemplateId(id);
    setForm({ ...POPUP_FIELD_DEFAULTS, ...(t?.defaults || {}), ...POPUP_SCROLL_BG_DEFAULTS });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/api/pages", { type: "popup", domain, html_content: outputHtml });
      setSaved(true);
    } catch { setError("Failed to save"); }
    finally { setSaving(false); }
  };

  useEffect(() => { setSaved(false); }, [outputHtml]);

  useEffect(() => {
    if (step === 3 && outputHtml && !saved && !saving) save();
  }, [step, outputHtml]);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
        <h1 className="text-xl font-bold text-slate-900">
          <i className="fa-solid fa-window-restore mr-2" style={{ color: "#059669" }} />Popup Widget Generator
        </h1>
        <p className="text-slate-500 text-sm mt-1">A general-purpose, fully customizable modal popup — pick a design and edit every visual property live</p>
        </div>
        <button type="button" onClick={onAddNew} className="btn-secondary text-sm whitespace-nowrap self-center">
          <i className="fa-solid fa-plus mr-1.5" />Add new
        </button>
      </div>

      <Stepper steps={STEPS} current={step} />

      {/* Step 0: Domain */}
      {step === 0 && (
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          <div className="card max-w-lg w-full">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                <i className="fa-solid fa-globe text-emerald-600" />
              </div>
              <h2 className="font-semibold text-slate-800">Your Domain</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Project name *</label>
                <input className="input" placeholder="e.g. Acme Popup" value={domain}
                  onChange={e => setDomain(e.target.value)} />
                {nameExists && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <i className="fa-solid fa-circle-exclamation" /> This name already exists — please choose another.
                  </p>
                )}
                {nameAvailable && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <i className="fa-solid fa-circle-check" /> Name is available.
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-1">Used to save and identify this project.</p>
              </div>

              {/* Create LP for Desktop */}
              <div className="border border-slate-100 rounded-lg p-3 bg-slate-50">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={desktopLP}
                    onChange={e => { setDesktopLP(e.target.checked); setLpError(""); }}
                    className="w-4 h-4 accent-emerald-500" />
                  <span className="text-xs font-semibold text-slate-700">
                    <i className="fa-solid fa-desktop mr-1.5 text-emerald-500" />Create LP for Desktop
                  </span>
                </label>
                {desktopLP && (
                  <div className="mt-3">
                    <p className="text-xs text-slate-500 mb-3">
                      Desktop visitors see an AI-generated blog landing page; the popup only shows on mobile.
                    </p>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Domain *</label>
                        <input className="input text-sm" placeholder="e.g. acme.com" value={lpDomain}
                          onChange={e => setLpDomain(e.target.value)} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Industry *</label>
                        <input className="input text-sm" placeholder="e.g. Fitness, Finance, Travel" value={lpIndustry}
                          onChange={e => setLpIndustry(e.target.value)} />
                      </div>
                    </div>
                    {blogLP ? (
                      <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-2">
                        <img src={blogLP.imageUrl} alt="" className="w-16 h-12 object-cover rounded" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-800 truncate">{blogLP.title}</p>
                          <p className="text-xs text-slate-400 truncate">{blogLP.subtitle}</p>
                        </div>
                        <button type="button" onClick={generateLanding} disabled={lpLoading}
                          className="text-xs text-emerald-500 hover:text-emerald-600 whitespace-nowrap">
                          <i className="fa-solid fa-rotate mr-1" />Regenerate
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={generateLanding} disabled={lpLoading || !lpDomain.trim() || !lpIndustry.trim()}
                        className="btn-secondary text-xs">
                        {lpLoading
                          ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Generating landing page...</>
                          : <><i className="fa-solid fa-wand-magic-sparkles mr-1.5" />Generate blog landing page</>}
                      </button>
                    )}
                    {lpError && <p className="text-red-500 text-xs mt-2">{lpError}</p>}
                    {blogLP && (
                      <div className="mt-2">
                        {lpImage ? (
                          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-2">
                            <img src={lpImage} alt="" className="w-16 h-12 object-cover rounded" />
                            <span className="text-xs text-slate-600 flex-1">Your blog image</span>
                            <button type="button" onClick={() => setLpImage("")} className="text-xs text-red-400 hover:text-red-500">Remove</button>
                          </div>
                        ) : (
                          <label className="inline-flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700 cursor-pointer">
                            <i className="fa-solid fa-image" />Upload your own blog image
                            <input type="file" accept="image/*" className="hidden" onChange={e => handleLpImage(e.target.files?.[0])} />
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button className="btn-primary px-6 font-semibold text-white"
                disabled={!domain.trim() || nameExists || lpLoading || (desktopLP && (!lpDomain.trim() || !lpIndustry.trim() || !blogLP))}
                onClick={() => setStep(1)}>
                Continue <i className="fa-solid fa-arrow-right ml-2" />
              </button>
            </div>
          </div>

          {/* Tips panel — large screens only */}
          <div className="hidden lg:block flex-1 max-w-sm pt-2">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              <i className="fa-solid fa-lightbulb mr-2 text-emerald-500" />Tips for great popups
            </h3>
            <ul className="space-y-3 text-sm text-slate-500">
              <li className="flex gap-2">
                <i className="fa-solid fa-circle-check text-emerald-500 mt-0.5" />
                <span>Trigger popups on exit-intent or after a short delay to avoid annoying first-time visitors.</span>
              </li>
              <li className="flex gap-2">
                <i className="fa-solid fa-circle-check text-emerald-500 mt-0.5" />
                <span>Keep your headline short and lead with the benefit, not the ask.</span>
              </li>
              <li className="flex gap-2">
                <i className="fa-solid fa-circle-check text-emerald-500 mt-0.5" />
                <span>Use a single, clear call-to-action button so visitors know exactly what to do next.</span>
              </li>
              <li className="flex gap-2">
                <i className="fa-solid fa-circle-check text-emerald-500 mt-0.5" />
                <span>Always give an easy way to dismiss the popup to keep the experience user-friendly.</span>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Step 1: Choose a design */}
      {step === 1 && (
        <div>
          <h2 className="font-semibold text-slate-800 mb-4">
            <i className="fa-solid fa-swatchbook mr-2 text-emerald-500" />Choose a design
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {POPUP_TEMPLATES.map(t => (
              <button key={t.id} onClick={() => chooseTemplate(t.id)}
                className={`card text-left p-3 transition-all hover:shadow-md cursor-pointer
                  ${templateId === t.id ? "ring-2 ring-emerald-500 border-emerald-200" : "hover:border-slate-300"}`}>
                <div className="w-full h-32 rounded-lg mb-3 overflow-hidden relative bg-slate-100 flex items-center justify-center">
                  <iframe
                    srcDoc={t.generate({ ...POPUP_FIELD_DEFAULTS, ...(t.defaults || {}), domain: domain || "example.com" })}
                    title={t.name}
                    sandbox=""
                    style={{ width: 700, height: 460, transform: "scale(0.26)", transformOrigin: "center", border: "none", pointerEvents: "none" }}
                  />
                </div>
                <p className="text-xs font-semibold text-slate-800">
                  <i className={`${t.icon} mr-1 text-emerald-400`} />{t.name}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">{t.description}</p>
                {templateId === t.id && <i className="fa-solid fa-circle-check text-emerald-500 mt-1" />}
              </button>
            ))}
          </div>
          <div className="flex gap-3 flex-wrap">
            <button className="btn-secondary" onClick={() => setStep(0)}>
              <i className="fa-solid fa-arrow-left mr-2" />Back
            </button>
            <button className="btn-primary" disabled={!templateId} onClick={() => setStep(2)}>
              Continue <i className="fa-solid fa-arrow-right ml-2" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Live editor */}
      {step === 2 && template && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <h2 className="font-semibold text-slate-800 mb-4">
              <i className="fa-solid fa-sliders mr-2 text-emerald-500" />Live Editor
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Image URL</label>
                <div className="flex gap-2">
                  <input className="input text-sm flex-1" placeholder="Image URL" value={form.imageUrl}
                    onChange={e => setForm({ ...form, imageUrl: e.target.value })} />
                  <label className="btn-secondary text-sm cursor-pointer whitespace-nowrap flex items-center">
                    <i className="fa-solid fa-upload mr-1" />Upload
                    <input type="file" accept="image/*" className="hidden"
                      onChange={e => { handleImageUpload(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                </div>
                {form.imageUrl && <img src={form.imageUrl} alt="preview" className="w-full h-24 object-cover rounded-lg border border-slate-200 mt-2" />}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Heading</label>
                <input className="input text-sm" value={form.heading} onChange={e => setForm({ ...form, heading: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Body Text</label>
                <textarea rows={3} className="input resize-none text-sm" value={form.bodyCopy}
                  onChange={e => setForm({ ...form, bodyCopy: e.target.value })} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Primary Button Text</label>
                  <input className="input text-sm" value={form.primaryText} onChange={e => setForm({ ...form, primaryText: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Primary Button URL</label>
                  <input className="input text-sm" placeholder="optional" value={form.primaryUrl} onChange={e => setForm({ ...form, primaryUrl: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Secondary Button Text</label>
                  <input className="input text-sm" placeholder="optional — leave blank to hide" value={form.secondaryText} onChange={e => setForm({ ...form, secondaryText: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Secondary Button URL</label>
                  <input className="input text-sm" placeholder="optional" value={form.secondaryUrl} onChange={e => setForm({ ...form, secondaryUrl: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "primaryColor",       label: "Primary Button Color" },
                  { key: "primaryTextColor",   label: "Primary Button Text Color" },
                  { key: "secondaryColor",     label: "Secondary Button Color" },
                  { key: "secondaryTextColor", label: "Secondary Button Text Color" },
                  { key: "bgColor",            label: "Card Background" },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs text-slate-500 mb-1">{f.label}</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={/^#/.test(form[f.key]) ? form[f.key] : "#ffffff"}
                        onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                        className="w-8 h-8 rounded border border-slate-200 cursor-pointer" />
                      <input className="input text-xs flex-1" value={form[f.key]}
                        onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Backdrop / overlay — color or image, with independent opacity + blur */}
              <div className="border border-slate-100 rounded-lg p-3 bg-slate-50">
                <p className="text-xs font-semibold text-slate-700 mb-2">
                  <i className="fa-solid fa-layer-group mr-1.5 text-emerald-500" />Backdrop
                </p>
                <div className="flex gap-2 mb-2">
                  <button type="button"
                    className={`flex-1 text-xs font-semibold py-1.5 rounded-md border ${form.overlayBgType === "color" ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200"}`}
                    onClick={() => setForm({ ...form, overlayBgType: "color" })}>
                    <i className="fa-solid fa-fill-drip mr-1" />Color
                  </button>
                  <button type="button"
                    className={`flex-1 text-xs font-semibold py-1.5 rounded-md border ${form.overlayBgType === "image" ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200"}`}
                    onClick={() => setForm({ ...form, overlayBgType: "image" })}>
                    <i className="fa-solid fa-image mr-1" />Image
                  </button>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <input type="color" value={/^#/.test(form.overlayBgColor) ? form.overlayBgColor : "#0f172a"}
                    onChange={e => setForm({ ...form, overlayBgColor: e.target.value })}
                    className="w-9 h-9 rounded border border-slate-200 cursor-pointer" />
                  <input className="input text-sm flex-1" placeholder="#0f172a" value={form.overlayBgColor}
                    onChange={e => setForm({ ...form, overlayBgColor: e.target.value })} />
                </div>

                {form.overlayBgType === "image" && (
                  <div className="space-y-2 mb-3">
                    <div className="flex gap-2">
                      <input className="input text-sm flex-1" placeholder="Backdrop image URL" value={form.overlayBgImage}
                        onChange={e => setForm({ ...form, overlayBgImage: e.target.value })} />
                      <label className="btn-secondary text-sm cursor-pointer whitespace-nowrap flex items-center">
                        <i className="fa-solid fa-upload mr-1" />Upload
                        <input type="file" accept="image/*" className="hidden" onChange={e => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = () => setForm(f => ({ ...f, overlayBgImage: reader.result }));
                          reader.readAsDataURL(file);
                          e.target.value = "";
                        }} />
                      </label>
                    </div>
                    {form.overlayBgImage && <img src={form.overlayBgImage} alt="backdrop preview" className="w-full h-24 object-cover rounded-lg border border-slate-200" />}
                    <p className="text-xs text-slate-400">The color above tints the image as a scrim, controlled by Opacity below.</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Opacity: {form.overlayOpacity}</label>
                    <input type="range" min="0" max="1" step="0.05" value={form.overlayOpacity}
                      onChange={e => setForm({ ...form, overlayOpacity: parseFloat(e.target.value) })} className="w-full" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Blur: {form.overlayBlur}px</label>
                    <input type="range" min="0" max="20" step="1" value={form.overlayBlur}
                      onChange={e => setForm({ ...form, overlayBlur: parseInt(e.target.value) || 0 })} className="w-full" />
                  </div>
                </div>
              </div>



              {/* BG Scroll: website-style background behind the fixed widget */}
              <div className="border border-slate-100 rounded-lg p-3 bg-white">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.scrollBgEnabled}
                    onChange={e => setForm({ ...form, scrollBgEnabled: e.target.checked })}
                    className="w-4 h-4 accent-emerald-500" />
                  <span className="text-xs font-semibold text-slate-700">
                    <i className="fa-solid fa-scroll mr-1.5 text-emerald-500" />BG Scroll
                  </span>
                </label>

                {form.scrollBgEnabled && (
                  <div className="mt-3 space-y-3">
                    <div className="flex gap-2">
                      <button type="button"
                        className={`flex-1 text-xs font-semibold py-1.5 rounded-md border ${form.scrollBgType === "color" ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200"}`}
                        onClick={() => setForm({ ...form, scrollBgType: "color" })}>
                        <i className="fa-solid fa-fill-drip mr-1" />Color
                      </button>
                      <button type="button"
                        className={`flex-1 text-xs font-semibold py-1.5 rounded-md border ${form.scrollBgType === "image" ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200"}`}
                        onClick={() => setForm({ ...form, scrollBgType: "image" })}>
                        <i className="fa-solid fa-image mr-1" />Image
                      </button>
                    </div>

                    {form.scrollBgType === "color" ? (
                      <div className="flex items-center gap-2">
                        <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(form.scrollBgColor) ? form.scrollBgColor : "#f8fafc"}
                          onChange={e => setForm({ ...form, scrollBgColor: e.target.value })}
                          className="w-10 h-9 rounded border border-slate-200 cursor-pointer" />
                        <input className="input text-sm flex-1" placeholder="#f8fafc" value={form.scrollBgColor || ""}
                          onChange={e => setForm({ ...form, scrollBgColor: e.target.value })} />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(!desktopLP ? [
                          { key: "desktop", label: "Desktop BG Images", images: form.scrollBgDesktopImages || ["", "", ""] },
                          { key: "phone", label: "Phone BG Images", images: form.scrollBgPhoneImages || ["", "", ""] }
                        ] : [
                          { key: "phone", label: "Phone BG Images", images: form.scrollBgPhoneImages || ["", "", ""] }
                        ]).map(group => (
                          <div key={group.key}>
                            <p className="text-xs font-medium text-slate-600 mb-1.5">{group.label}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              {[0, 1, 2].map(i => (
                                <div key={i} className="border border-slate-200 rounded-lg p-2 bg-slate-50">
                                  {group.images?.[i] ? (
                                    <div className="relative">
                                      <img src={group.images[i]} alt={`${group.label} ${i + 1}`} className="w-full h-20 object-cover rounded-md" />
                                      <button type="button"
                                        onClick={() => {
                                          const key = group.key === "desktop" ? "scrollBgDesktopImages" : "scrollBgPhoneImages";
                                          const images = [...(form[key] || ["", "", ""])];
                                          images[i] = "";
                                          setForm({ ...form, [key]: images });
                                        }}
                                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center hover:bg-slate-900">
                                        x
                                      </button>
                                    </div>
                                  ) : (
                                    <label className="h-20 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-200 rounded-md cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/30">
                                      <i className="fa-solid fa-upload text-slate-400" />
                                      <span className="text-[11px] text-slate-500">Image {i + 1}</span>
                                      <input type="file" accept="image/*" className="hidden"
                                        onChange={e => { handleScrollBgImage(e.target.files?.[0], i, group.key); e.target.value = ""; }} />
                                    </label>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Blury: {form.scrollBgBlur || 0}px</label>
                        <input type="range" min="0" max="18" step="1" value={form.scrollBgBlur || 0}
                          onChange={e => setForm({ ...form, scrollBgBlur: parseInt(e.target.value, 10) || 0 })}
                          className="w-full" />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Opacity: {form.scrollBgOpacity ?? 0.25}</label>
                        <input type="range" min="0" max="0.8" step="0.05" value={form.scrollBgOpacity ?? 0.25}
                          onChange={e => setForm({ ...form, scrollBgOpacity: parseFloat(e.target.value) })}
                          className="w-full" />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Border Radius (px)</label>
                  <input type="number" className="input text-xs" value={form.borderRadius}
                    onChange={e => setForm({ ...form, borderRadius: parseInt(e.target.value) || 0 })} />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Padding (px)</label>
                  <input type="number" className="input text-xs" value={form.padding}
                    onChange={e => setForm({ ...form, padding: parseInt(e.target.value) || 0 })} />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Box Width (px)</label>
                  <input type="number" className="input text-xs" placeholder="auto" value={form.boxWidth}
                    onChange={e => setForm({ ...form, boxWidth: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Box Height (px)</label>
                  <input type="number" className="input text-xs" placeholder="auto" value={form.boxHeight}
                    onChange={e => setForm({ ...form, boxHeight: e.target.value })} />
                </div>
              </div>

              {templateId === "scratch-card" && (
                <div className="border border-slate-100 rounded-lg p-3 bg-amber-50/50">
                  <p className="text-xs font-semibold text-slate-700 mb-2">
                    <i className="fa-solid fa-gift mr-1.5 text-amber-500" />Scratch Card
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Reveal Heading (hidden under the foil)</label>
                      <input className="input text-sm" value={form.revealHeading}
                        onChange={e => setForm({ ...form, revealHeading: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Scratch Label</label>
                        <input className="input text-sm" value={form.scratchLabel}
                          onChange={e => setForm({ ...form, scratchLabel: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Foil Color</label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(form.scratchColor) ? form.scratchColor : "#d6b866"}
                            onChange={e => setForm({ ...form, scratchColor: e.target.value })}
                            className="w-8 h-8 rounded border border-slate-200 cursor-pointer" />
                          <input className="input text-xs flex-1" value={form.scratchColor}
                            onChange={e => setForm({ ...form, scratchColor: e.target.value })} />
                        </div>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!form.confettiEnabled}
                        onChange={e => setForm({ ...form, confettiEnabled: e.target.checked })}
                        className="w-4 h-4 accent-amber-500" />
                      <span className="text-xs font-semibold text-slate-700">
                        🎉 Celebration confetti burst on reveal
                      </span>
                    </label>
                    <p className="text-xs text-slate-400">Image URL above (optional) shows as a logo on top of the card. The reveal button uses the Primary Button text/color/URL.</p>
                  </div>
                </div>
              )}

              {templateId === "split-confirm" && (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Image Panel Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={/^#/.test(form.imagePanelColor) ? form.imagePanelColor : "#87a06b"}
                      onChange={e => setForm({ ...form, imagePanelColor: e.target.value })}
                      className="w-8 h-8 rounded border border-slate-200 cursor-pointer" />
                    <input className="input text-xs flex-1" value={form.imagePanelColor}
                      onChange={e => setForm({ ...form, imagePanelColor: e.target.value })} />
                  </div>
                </div>
              )}

              {/* Advanced — Heading / Body / Buttons (same options as Cookie / Age-Verification / Newsletter) */}
              <div className="border border-slate-100 rounded-lg p-3 bg-slate-50">
                <label className="flex items-center gap-2 mb-3 cursor-pointer">
                  <input type="checkbox" checked={form.advancedEnabled}
                    onChange={e => setForm({ ...form, advancedEnabled: e.target.checked })}
                    className="w-4 h-4 accent-emerald-500" />
                  <span className="text-xs font-semibold text-slate-700">
                    <i className="fa-solid fa-sliders mr-1.5 text-emerald-500" />Advanced — Heading, Body &amp; Buttons
                  </span>
                </label>
                {form.advancedEnabled && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-medium text-slate-600 mb-1.5">Content Alignment</p>
                        <div className="flex gap-1">
                          {[
                            { v: "left",   icon: "fa-align-left" },
                            { v: "center", icon: "fa-align-center" },
                            { v: "right",  icon: "fa-align-right" },
                          ].map(o => (
                            <button key={o.v} type="button"
                              className={`flex-1 py-1.5 rounded-md border text-xs ${form.contentAlign === o.v ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200"}`}
                              onClick={() => setForm({ ...form, contentAlign: form.contentAlign === o.v ? "" : o.v })}>
                              <i className={`fa-solid ${o.icon}`} />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-600 mb-1.5">Button Layout</p>
                        <div className="flex gap-1">
                          <button type="button"
                            className={`flex-1 py-1.5 rounded-md border text-xs ${form.buttonLayout === "row" ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200"}`}
                            onClick={() => setForm({ ...form, buttonLayout: form.buttonLayout === "row" ? "" : "row" })}>
                            <i className="fa-solid fa-grip-lines mr-1" />Inline
                          </button>
                          <button type="button"
                            className={`flex-1 py-1.5 rounded-md border text-xs ${form.buttonLayout === "column" ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200"}`}
                            onClick={() => setForm({ ...form, buttonLayout: form.buttonLayout === "column" ? "" : "column" })}>
                            <i className="fa-solid fa-grip-lines-vertical mr-1" />Column
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: "headingColor", label: "Heading Color" },
                        { key: "subColor",     label: "Body Text Color" },
                      ].map(f => (
                        <div key={f.key}>
                          <label className="block text-xs text-slate-500 mb-1">{f.label}</label>
                          <div className="flex items-center gap-2">
                            <input type="color" value={form[f.key] || "#000000"}
                              onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                              className="w-8 h-8 rounded border border-slate-200 cursor-pointer" />
                            <input className="input text-xs flex-1" placeholder="auto" value={form[f.key]}
                              onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <TypographyRow title="Typography (Heading)" form={form} setForm={setForm}
                      sizeKey="fontSize" weightKey="fontWeight" formatKey="format" />
                    <TypographyRow title="Typography (Body)" form={form} setForm={setForm}
                      sizeKey="subFontSize" weightKey="subFontWeight" formatKey="subFormat" />
                    <TypographyRow title="Typography (Buttons)" form={form} setForm={setForm}
                      sizeKey="btnFontSize" weightKey="btnFontWeight" formatKey="btnFormat" />
                    <div>
                      <p className="text-xs font-medium text-slate-600 mb-1.5">Button Size &amp; Spacing</p>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Width</label>
                          <select className="input text-xs" value={form.buttonWidth}
                            onChange={e => setForm({ ...form, buttonWidth: e.target.value })}>
                            <option value="">Auto</option>
                            <option value="full">Full width</option>
                            <option value="120">120px</option>
                            <option value="160">160px</option>
                            <option value="200">200px</option>
                            <option value="240">240px</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Padding X</label>
                          <input type="number" className="input text-xs" placeholder="20" value={form.buttonPaddingX}
                            onChange={e => setForm({ ...form, buttonPaddingX: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Padding Y</label>
                          <input type="number" className="input text-xs" placeholder="12" value={form.buttonPaddingY}
                            onChange={e => setForm({ ...form, buttonPaddingY: e.target.value })} />
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-600 mb-1.5">Corners &amp; Shadow</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Button radius</label>
                          <input type="number" className="input text-xs" placeholder="auto" value={form.btnRadius}
                            onChange={e => setForm({ ...form, btnRadius: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Box shadow</label>
                          <select className="input text-xs" value={form.boxShadow}
                            onChange={e => setForm({ ...form, boxShadow: e.target.value })}>
                            <option value="">Default</option>
                            <option value="none">None</option>
                            <option value="soft">Soft</option>
                            <option value="strong">Strong</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button className="btn-secondary" onClick={() => setStep(1)}>
                <i className="fa-solid fa-arrow-left mr-1" />Back
              </button>
              <button className="btn-primary" onClick={() => setStep(3)}>
                Preview <i className="fa-solid fa-arrow-right ml-1" />
              </button>
            </div>
          </div>

          {/* Live preview */}
          <div className="card self-start lg:sticky lg:top-6">
            <h2 className="font-semibold text-slate-800 mb-3 text-sm">
              <i className="fa-solid fa-eye mr-2 text-emerald-500" />Live Preview
            </h2>
            <DevicePreviewFrame html={popupHtml} height={480} title="preview" />
          </div>
        </div>
      )}

      {/* Step 3: Preview + download + save */}
      {step === 3 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-800">
              <i className="fa-solid fa-eye mr-2 text-emerald-500" />Final Preview
            </h2>
            <div className="flex gap-3 items-center">
              {lpActive && (
                <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                  <button onClick={() => setPreviewMode("desktop")} title="Desktop view"
                    className={`px-2.5 py-1 rounded-md text-sm ${previewMode === "desktop" ? "bg-white text-emerald-600 shadow-sm" : "text-slate-500"}`}>
                    <i className="fa-solid fa-desktop" />
                  </button>
                  <button onClick={() => setPreviewMode("mobile")} title="Phone view"
                    className={`px-2.5 py-1 rounded-md text-sm ${previewMode === "mobile" ? "bg-white text-emerald-600 shadow-sm" : "text-slate-500"}`}>
                    <i className="fa-solid fa-mobile-screen-button" />
                  </button>
                </div>
              )}
              <button onClick={() => setStep(2)} className="btn-secondary text-sm">
                <i className="fa-solid fa-arrow-left mr-1" />Edit
              </button>
              <button onClick={() => downloadAsZip(outputHtml, `popup-${domain}`)} className="btn-secondary text-sm">
                <i className="fa-solid fa-download mr-1" />Download ZIP
              </button>
              <button onClick={save} disabled={saving || saved} className="btn-primary text-sm">
                {saving ? <><i className="fa-solid fa-spinner fa-spin mr-1" />Saving…</>
                 : saved ? <><i className="fa-solid fa-check mr-1" />Saved!</>
                 :         <><i className="fa-solid fa-floppy-disk mr-1" />Save Page</>}
              </button>
            </div>
          </div>
          {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
          {lpActive && previewMode === "mobile" ? (
            <div className="flex justify-center bg-slate-100 rounded-xl py-6" style={{ minHeight: 600 }}>
              <div className="bg-black rounded-[2rem] p-2 shadow-xl" style={{ width: 390 }}>
                <iframe srcDoc={previewHtml} title="Final preview (phone)" className="w-full bg-white rounded-[1.5rem]" style={{ height: 720 }} sandbox="allow-scripts" />
              </div>
            </div>
          ) : (
            <div className="card">
              <DevicePreviewFrame html={previewHtml} height={520} title="Final preview" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
